import { createRouter as _createRouter, createWebHistory, createMemoryHistory } from 'vue-router'
import { allowEditorPreview } from './previewRoute'
import { LEGACY_ABOUT_SLUGS } from './utils/aboutSections'

/**
 * Storefront router.
 *
 * Every content route renders `CmsPageView`, which resolves the matching CMS
 * page from the Blox API and renders its persisted block tree (nav + sections +
 * footer). This lets editors compose and reorder every page in the admin, and
 * lets the client navigate between CMS pages without a full reload — the same
 * architecture used by sibling projects (e.g. sellameir).
 *
 * Only genuinely app-only utility screens (print helper) stay on a static view.
 */
export function createRouter(mode: 'web' | 'memory' = 'web', _initialUrl?: string) {
	// `createMemoryHistory`'s first argument is the router BASE, not the initial
	// location. Passing the page URL resolved every prerendered link relative to
	// that page (`to="/contact"` → `/about/contact` on `/about`). blox-ssg pushes
	// the URL itself, so the base must stay '/'.
	const history = mode === 'memory'
		? createMemoryHistory()
		: createWebHistory(import.meta.env.BASE_URL)

	const router = _createRouter({
		history,
		routes: [
			// Legacy reconstruction URLs kept as permanent client-side redirects.
			{ path: '/books/:slug', redirect: to => `/book/${String(to.params.slug)}` },
			{ path: '/holiday/:slug', redirect: to => `/holidays/${String(to.params.slug)}` },
			{ path: '/category/:slug', redirect: to => `/categories/${String(to.params.slug)}` },

			// App-only utility view (not a CMS page).
			{ path: '/print', component: () => import('@/views/Print.vue') },

			// Own the editor preview before the published Blox runtime can add its
			// route. That runtime redirects `/_blox_preview` in the top window, but
			// its guard compares `window.parent` and therefore also redirects the
			// framed designer preview. Registering this route first makes that
			// guard unreachable.
			{
				path: '/_blox_preview',
				name: 'blox:preview',
				component: () => import('@bagelink/blox').then(module => module.PreviewApp),
				beforeEnter: allowEditorPreview,
			},

			// Blox installs the single CMS fallback route.
		],
		scrollBehavior(to) {
			if (to.hash) return { behavior: 'smooth', el: to.hash, top: 0 }
			return { top: 0, left: 0 }
		},
	})

	/**
	 * The old site served each About record at its own URL (`/about/staff`).
	 * Netlify redirects those in `public/_redirects`; this mirrors the same
	 * mapping for client-side navigation and local dev, where no host redirect
	 * exists — otherwise the CMS catch-all resolves `/about/staff` to the 404
	 * page. Unknown `/about/<slug>` paths stay 404s.
	 */
	router.beforeEach((to) => {
		if (!to.path.startsWith('/about/')) return
		const slug = decodeURIComponent(to.path.slice('/about/'.length)).replace(/\/+$/, '')
		if (!LEGACY_ABOUT_SLUGS.includes(slug)) return
		return {
			path: '/about',
			query: slug === 'about-us' ? {} : { section: slug },
			replace: true,
		}
	})

	return router
}

export default createRouter()
